import { useEffect, useRef, type MutableRefObject } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { offerFormSchema, type OfferFormValues } from "@/features/offers/form-schema";
import { definitionsToRows, rowsToDefinitions } from "@/features/custom-fields/form-schema";
import {
  unreadableCustomFields,
  type CustomFieldDefinition,
  type StoredCustomFields,
} from "@/domain/custom-fields/types";
import type { PartnerOffer, SaleWindow } from "@/domain/offer/types";
import type { OfferActivityOption } from "@/data/repos/partnerOffersRepo";
import { formatDate } from "@/lib/time";
import { templateSaleStartValue } from "@/features/offers/sale-start-prefill";
import { templateSaleEndValue } from "@/features/offers/sale-end-prefill";
import { slotEnd } from "@/features/offers/slot-end";
import { saleCloseOf } from "@/domain/activity/rules";
import { endTimeAfter } from "@/domain/offer/series";

/** Publishing splits this into the slot and the offer; editing sends only what
 *  the offer itself carries. */
export interface OfferDraft {
  activityId: number;
  startTime: Date;
  endTime: Date;
  capacity: number;
  priceDiscounted: number;
  saleWindow: SaleWindow;
  customFields: CustomFieldDefinition[] | null | undefined;
}

const emptyValues: OfferFormValues = {
  activity_id: "",
  date: new Date(),
  start_time: "",
  end_time: "",
  price_discounted: "",
  max_slots: "1",
  sale_start: "",
  sale_end: "",
  custom_fields_mode: "inherit",
  custom_fields: [],
};

function pickerValue(bound: Date | null): string {
  return bound ? formatDate(bound, "yyyy-MM-dd'T'HH:mm") : "";
}

function numberValue(value: number | null, whenAbsent: string): string {
  return value === null ? whenAbsent : String(value);
}

function offerToFormValues(offer: PartnerOffer): OfferFormValues {
  return {
    activity_id: String(offer.activityId),
    date: offer.startTime,
    start_time: formatDate(offer.startTime, "HH:mm"),
    end_time: formatDate(offer.endTime, "HH:mm"),
    price_discounted: offer.priceDiscounted?.toString() ?? "",
    max_slots: String(offer.capacity),
    sale_start: pickerValue(offer.saleWindow.startsAt),
    sale_end: pickerValue(offer.saleWindow.endsAt),
    custom_fields_mode: customFieldsMode(offer.customFields),
    custom_fields: definitionsToRows(editableDefinitions(offer.customFields)),
  };
}

function customFieldsMode(stored: StoredCustomFields): OfferFormValues["custom_fields_mode"] {
  if (stored === null) return "inherit";
  if (stored === unreadableCustomFields) return "unreadable";
  return "override";
}

function editableDefinitions(stored: StoredCustomFields): CustomFieldDefinition[] | null {
  return stored === unreadableCustomFields ? null : stored;
}

/** Undefined omits the property from the PATCH, and an omitted property leaves
 *  the stored definitions untouched — the only way an override this portal
 *  cannot render survives an edit of the price beside it. */
function draftCustomFields(values: OfferFormValues): CustomFieldDefinition[] | null | undefined {
  if (values.custom_fields_mode === "unreadable") return undefined;
  if (values.custom_fields_mode === "inherit") return null;
  return rowsToDefinitions(values.custom_fields);
}

export function useOfferForm(
  offer: PartnerOffer | null | undefined,
  templates: OfferActivityOption[] = [],
) {
  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema),
    mode: "onBlur",
    defaultValues: emptyValues,
  });
  const isPublished = !!offer;
  const selectedTemplateId = form.watch("activity_id");
  const slotDay = form.watch("date");
  const slotStartTime = form.watch("start_time");
  const slotEndTime = form.watch("end_time");
  // The prefill is a copy taken once per chosen template, not a binding: a
  // refetch of the same šablona must leave the partner's own numbers standing,
  // and the templates may still be loading when the picker is already
  // preselected.
  const prefilledFrom = useRef<string | null>(null);
  // What the šablona filled in and the partner has not touched since. The end,
  // the selling start and the selling end follow the slot's hours too, so they
  // are rewritten outside a re-pick — but never over a value the partner typed.
  const prefilledEndTime = useRef("");
  const prefilledSaleStart = useRef("");
  const prefilledSaleEnd = useRef("");

  useEffect(() => {
    prefilledFrom.current = null;
    prefilledEndTime.current = "";
    prefilledSaleStart.current = "";
    prefilledSaleEnd.current = "";
    if (offer) form.reset(offerToFormValues(offer));
    else form.reset(emptyValues);
  }, [offer, form]);

  useEffect(() => {
    if (isPublished) return;
    const template = templates.find((t) => String(t.id) === selectedTemplateId);
    if (!template) return;

    function prefill(
      field: "end_time" | "sale_start" | "sale_end",
      prefilled: MutableRefObject<string>,
      value: string,
      overwritesTypedValue: boolean,
    ) {
      if (!overwritesTypedValue && form.getValues(field) !== prefilled.current) return;
      prefilled.current = value;
      form.setValue(field, value);
    }

    // Picking another šablona is a deliberate re-pick, so it overwrites what the
    // partner typed; every field is rewritten, a null default back to its empty
    // value, or the previous šablona's number would be published as this one's.
    const isRepick = prefilledFrom.current !== selectedTemplateId;
    if (isRepick) {
      prefilledFrom.current = selectedTemplateId;
      form.setValue("price_discounted", numberValue(template.defaultPriceDiscounted, ""));
      // Empty, never the blank form's "1": a šablona that names no places has
      // not said "one place", and a prefilled 1 publishes a termín for one
      // person the partner never chose.
      form.setValue("max_slots", numberValue(template.defaultCapacity, ""));
    }

    prefill(
      "end_time",
      prefilledEndTime,
      endTimeAfter(slotStartTime, template.defaultDurationMinutes),
      false,
    );
    prefill(
      "sale_start",
      prefilledSaleStart,
      templateSaleStartValue(slotDay, slotStartTime, template.saleLeadTimeHours),
      isRepick,
    );
    prefill(
      "sale_end",
      prefilledSaleEnd,
      templateSaleEndValue(slotDay, slotStartTime, form.getValues("end_time"), saleCloseOf(template)),
      isRepick,
    );
  }, [selectedTemplateId, slotDay, slotStartTime, slotEndTime, templates, isPublished, form]);

  function buildDraft(values: OfferFormValues): OfferDraft {
    const day = formatDate(values.date, "yyyy-MM-dd");
    const startTime = new Date(`${day}T${values.start_time}:00`);
    return {
      activityId: parseInt(values.activity_id, 10),
      startTime,
      endTime: slotEnd(day, values.start_time, values.end_time),
      capacity: parseInt(values.max_slots, 10),
      priceDiscounted: parseFloat(values.price_discounted),
      // A blank selling time is sent as null so editing can clear a stored
      // bound; the API then leaves that end to its own default.
      saleWindow: {
        startsAt: values.sale_start ? new Date(values.sale_start) : null,
        endsAt: values.sale_end ? new Date(values.sale_end) : null,
      },
      customFields: draftCustomFields(values),
    };
  }

  return { form, buildDraft };
}
